import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import "./UpdateEffortsSavingsAICard.scss";
import aiChatlogo from "../../../../../../assets/Images/aiChatlogo.png";
import { OrdersService } from "../../../../../../api/services/orders.service";
import { LookupService } from "../../../../../../api/services/lookup.service";
import ActiveDirectoryUserSearch from "../../../../../OrdersDetails/ActiveDirectoryUserSearch";
import {
  getFieldValidation,
  validateContributorUniqueness,
  isContributorField,
} from "../../../../../OrdersDetails/fieldValidations.config";

const normalizeFieldIdentifier = (value) =>
  String(value || "")
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase();

const isEmptyFieldValue = (value) => {
  if (Array.isArray(value)) {
    return value.every((item) => String(item ?? "").trim() === "");
  }

  return String(value ?? "").trim() === "";
};

const getInputTooltipTitle = (value) => {
  if (isEmptyFieldValue(value)) {
    return undefined;
  }

  return String(value);
};

const APPLICATION_USED_LOOKUP_REGION_FIELD_CANDIDATES = [
  "RegionId",
  "Area",
  "Region",
];

const isManuallyEditableAutoPopulatedSectionField = (
  sectionName,
  fieldName,
) => {
  const normalizedFieldName = normalizeFieldIdentifier(fieldName);

  if (sectionName === "Other/Effort") {
    return normalizedFieldName === "resourcename";
  }

  if (sectionName === "Other/Saving") {
    return (
      normalizedFieldName === "hourstosave" ||
      normalizedFieldName === "username"
    );
  }

  return false;
};

const getComparableFieldValue = (field, value) => {
  const normalizedValue = String(value ?? "")
    .trim()
    .toLowerCase();

  if (!normalizedValue || !Array.isArray(field?.fieldValueList)) {
    return String(value ?? "").trim();
  }

  const matchingOption = field.fieldValueList.find((option) => {
    const optionValue =
      typeof option === "object"
        ? option.value || option.field || option.id
        : option;
    const optionLabel =
      typeof option === "object"
        ? option.label ||
          option.field ||
          option.value ||
          option.name ||
          option.id
        : option;

    return [optionValue, optionLabel].some(
      (candidate) =>
        String(candidate ?? "")
          .trim()
          .toLowerCase() === normalizedValue,
    );
  });

  if (!matchingOption) {
    return String(value ?? "").trim();
  }

  return String(
    typeof matchingOption === "object"
      ? matchingOption.label ||
          matchingOption.field ||
          matchingOption.value ||
          matchingOption.name ||
          matchingOption.id ||
          ""
      : matchingOption,
  ).trim();
};

const normalizeLookupMatchValue = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const getApplicationUsedHoursReasonRegions = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  return Array.isArray(response?.data) ? response.data : [];
};
//Coplot Suggestion Change if need to Revert back to previous version

const getApplicationUsedHoursReasonRegionMatchValues = (region) => {
  return [region?.regionId, region?.regionGuid, region?.regionName]
    .map(normalizeLookupMatchValue)
    .filter(Boolean);
};

const getApplicationUsedHoursReasonMatchValues = (applicationUsed) => {
  return [applicationUsed?.applicationUsedId, applicationUsed?.applicationUsed]
    .map(normalizeLookupMatchValue)
    .filter(Boolean);
};

const getUpdateOrderValidationWarnings = (response) => {
  // AXON-CHAT-VALIDATION-WARNINGS: normalize warning arrays from resolved updateOrder responses.
  return [response?.validationWarnings, response?.data?.validationWarnings]
    .flatMap((warnings) => (Array.isArray(warnings) ? warnings : []))
    .map((warning) => String(warning || "").trim())
    .filter(Boolean);
};

const buildApplicationUsedHoursReasonEntries = (regions = []) => {
  return regions.flatMap((region) =>
    (Array.isArray(region?.applicationUsedData)
      ? region.applicationUsedData
      : []
    ).map((applicationUsed) => ({
      ...applicationUsed,
      regionId: applicationUsed?.regionId ?? region?.regionId,
      regionGuid: region?.regionGuid,
      regionName: region?.regionName,
    })),
  );
};

const buildSavingReasonOptionsFromLookup = (applicationUsed) => {
  if (!Array.isArray(applicationUsed?.savingHoursReason)) {
    return [];
  }

  return applicationUsed.savingHoursReason
    .filter((option) => option?.id != null && option?.reason)
    .map((option) => ({
      value: String(option.id),
      field: option.reason,
      isDisabled: false,
    }));
};

const formatSavingDefaultHoursValue = (value) => {
  if (value === undefined || value === null) {
    return "";
  }

  return String(value).trim();
};

const getFieldOptionMatchValues = (option) => {
  if (typeof option !== "object" || option === null) {
    const normalizedValue = normalizeLookupMatchValue(option);
    return normalizedValue ? [normalizedValue] : [];
  }

  return [option.value, option.field, option.label, option.name, option.id]
    .map(normalizeLookupMatchValue)
    .filter(Boolean);
};

const getFieldOptionLabel = (option) => {
  if (typeof option !== "object" || option === null) {
    return option;
  }

  return (
    option.label ?? option.field ?? option.value ?? option.name ?? option.id
  );
};

const isOtherLikeReasonValue = (value) => {
  const normalizedValue = normalizeLookupMatchValue(value);

  return normalizedValue === "other" || normalizedValue === "others";
};

const isOtherLikeReasonSelection = (field, value) => {
  const normalizedValue = normalizeLookupMatchValue(value);

  if (!normalizedValue) {
    return false;
  }

  const matchingOption = Array.isArray(field?.fieldValueList)
    ? field.fieldValueList.find((option) =>
        getFieldOptionMatchValues(option).includes(normalizedValue),
      )
    : null;
  const selectedLabel = matchingOption
    ? getFieldOptionLabel(matchingOption)
    : value;

  return isOtherLikeReasonValue(selectedLabel) || isOtherLikeReasonValue(value);
};

const getOrderDetailsFieldValueByCandidates = (
  orderDetails,
  candidates = [],
) => {
  const normalizedCandidates = candidates
    .map(normalizeFieldIdentifier)
    .filter(Boolean);

  if (!Array.isArray(orderDetails) || normalizedCandidates.length === 0) {
    return "";
  }

  for (const section of orderDetails) {
    const pagefields = Array.isArray(section?.pagefields)
      ? section.pagefields
      : [];

    const matchingField = pagefields.find((field) => {
      const normalizedFieldName = normalizeFieldIdentifier(field?.field);
      const normalizedLabel = normalizeFieldIdentifier(field?.labelToShowOnUI);

      return normalizedCandidates.some(
        (candidate) =>
          candidate === normalizedFieldName || candidate === normalizedLabel,
      );
    });

    if (!matchingField) {
      continue;
    }

    return matchingField?.fieldValue ?? "";
  }

  return "";
};

export const UpdateEffortsSavingsAICard = ({
  message,
  onActionClick,
  orderDetails,
  onRefreshOrderDetails,
}) => {
  const user = useSelector((state) => state.user?.user);
  const selectedRole = useSelector((state) => state.user?.selectedRole);
  const [formValues, setFormValues] = useState({});
  const [fieldValidationErrors, setFieldValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [
    applicationUsedHoursReasonLookupResponse,
    setApplicationUsedHoursReasonLookupResponse,
  ] = useState(null);

  const now = new Date();
  const day = now.toLocaleDateString(undefined, {
    weekday: "long",
  });
  const time = now.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  const userEmail =
    user?.email ||
    user?.mail ||
    user?.username ||
    user?.preferred_username ||
    "";
  const currentUserRole = useMemo(() => {
    const roleSource =
      selectedRole?.roleName ||
      selectedRole?.name ||
      (typeof selectedRole === "string" ? selectedRole : "") ||
      user?.role ||
      "";

    return String(roleSource).trim();
  }, [selectedRole, user?.role]);

  const normalizedCurrentUserRole = useMemo(
    () => currentUserRole.toUpperCase(),
    [currentUserRole],
  );

  const isDidRole = useMemo(
    () => !normalizedCurrentUserRole || normalizedCurrentUserRole === "DID",
    [normalizedCurrentUserRole],
  );

  const isRegionalLead = useMemo(
    () => normalizedCurrentUserRole === "REGIONAL LEAD",
    [normalizedCurrentUserRole],
  );

  // Determine section type from message
  const sectionType = message.effortsSavingsType || "efforts"; // 'efforts' or 'savings'
  const sectionName =
    sectionType === "efforts" ? "Other/Effort" : "Other/Saving";
  const displayName = sectionType === "efforts" ? "Efforts" : "Savings";

  const sectionConfig = useMemo(() => {
    if (!orderDetails || !Array.isArray(orderDetails)) {
      return null;
    }

    return orderDetails.find((tab) => tab.section === sectionName) || null;
  }, [orderDetails, sectionName]);

  // Extract editable fields from orderDetails for the specific section
  const sectionFields = useMemo(() => {
    if (!sectionConfig?.pagefields) {
      return [];
    }

    return sectionConfig.pagefields
      .filter((field) => {
        // return field.isEditable === true && field.isNonEditableByDA === false;
        const isManuallyEditable = isManuallyEditableAutoPopulatedSectionField(
          sectionName,
          field?.field,
        );

        if (field?.isNonEditableByDA === true) {
          return false;
        }

        if (isManuallyEditable) {
          return true;
        }

        if (isRegionalLead) {
          if (typeof field?.isEditableByRL === "boolean") {
            return field.isEditableByRL;
          }

          return true;
        }

        if (isDidRole) {
          if (typeof field?.isEditable === "boolean") {
            return field.isEditable;
          }

          if (typeof field?.editable === "boolean") {
            return field.editable;
          }
        }

        return true;
      })
      .map((field) => ({
        label: field.labelToShowOnUI || field.field,
        fieldKey: field.field,
        dataType: field.dataType,
        fieldValueList: field.fieldValueList || [],
        fieldValue: field.fieldValue ?? "",
      }));
  }, [isDidRole, isRegionalLead, sectionConfig, sectionName]);

  useEffect(() => {
    let isCancelled = false;

    if (sectionType !== "savings") {
      setApplicationUsedHoursReasonLookupResponse(null);
      return () => {
        isCancelled = true;
      };
    }

    const fetchApplicationUsedHoursReasonLookup = async () => {
      try {
        const response =
          await LookupService.getApplicationUsedHoursReasonData();

        if (isCancelled) {
          return;
        }

        setApplicationUsedHoursReasonLookupResponse(response || null);
      } catch (error) {
        if (isCancelled) {
          return;
        }

        console.error(
          "Failed to fetch application used hours and reason lookup data:",
          error,
        );
        setApplicationUsedHoursReasonLookupResponse(null);
      }
    };

    fetchApplicationUsedHoursReasonLookup();

    return () => {
      isCancelled = true;
    };
  }, [sectionType]);

  const applicationUsedHoursReasonLookupRegions = useMemo(
    () =>
      getApplicationUsedHoursReasonRegions(
        applicationUsedHoursReasonLookupResponse,
      ),
    [applicationUsedHoursReasonLookupResponse],
  );

  const allApplicationUsedHoursReasonLookupEntries = useMemo(
    () =>
      buildApplicationUsedHoursReasonEntries(
        applicationUsedHoursReasonLookupRegions,
      ),
    [applicationUsedHoursReasonLookupRegions],
  );

  const applicationUsedHoursReasonLookupEntries = useMemo(() => {
    const selectedRegionValue = getOrderDetailsFieldValueByCandidates(
      orderDetails,
      APPLICATION_USED_LOOKUP_REGION_FIELD_CANDIDATES,
    );
    const normalizedSelectedRegionValue =
      normalizeLookupMatchValue(selectedRegionValue);

    if (!normalizedSelectedRegionValue) {
      return allApplicationUsedHoursReasonLookupEntries;
    }

    const matchedRegion =
      applicationUsedHoursReasonLookupRegions.find((region) =>
        getApplicationUsedHoursReasonRegionMatchValues(region).some(
          (candidateValue) => candidateValue === normalizedSelectedRegionValue,
        ),
      ) || null;

    if (!matchedRegion) {
      return allApplicationUsedHoursReasonLookupEntries;
    }

    return buildApplicationUsedHoursReasonEntries([matchedRegion]);
  }, [
    allApplicationUsedHoursReasonLookupEntries,
    applicationUsedHoursReasonLookupRegions,
    orderDetails,
  ]);

  const findMatchedApplicationUsedHoursReasonEntry = useCallback(
    (applicationField, selectedValue) => {
      const normalizedSelectedValue = normalizeLookupMatchValue(selectedValue);

      if (!normalizedSelectedValue) {
        return null;
      }

      const matchCandidates = new Set([normalizedSelectedValue]);

      (Array.isArray(applicationField?.fieldValueList)
        ? applicationField.fieldValueList
        : []
      ).forEach((option) => {
        const optionMatchValues = getFieldOptionMatchValues(option);

        if (!optionMatchValues.includes(normalizedSelectedValue)) {
          return;
        }

        optionMatchValues.forEach((optionValue) =>
          matchCandidates.add(optionValue),
        );
      });

      const findMatchingEntry = (entries = []) => {
        return (
          entries.find((applicationUsed) =>
            getApplicationUsedHoursReasonMatchValues(applicationUsed).some(
              (candidateValue) => matchCandidates.has(candidateValue),
            ),
          ) || null
        );
      };

      return (
        findMatchingEntry(applicationUsedHoursReasonLookupEntries) ||
        findMatchingEntry(allApplicationUsedHoursReasonLookupEntries)
      );
    },
    [
      allApplicationUsedHoursReasonLookupEntries,
      applicationUsedHoursReasonLookupEntries,
    ],
  );

  const matchedSavingLookupEntry = useMemo(() => {
    if (sectionType !== "savings") {
      return null;
    }

    const applicationField = sectionFields.find(
      (field) =>
        normalizeFieldIdentifier(field.fieldKey) === "applicationusedid",
    );

    if (!applicationField) {
      return null;
    }

    const selectedApplicationValue = Object.prototype.hasOwnProperty.call(
      formValues,
      applicationField.fieldKey,
    )
      ? (formValues[applicationField.fieldKey] ?? "")
      : applicationField.fieldValue || "";

    return findMatchedApplicationUsedHoursReasonEntry(
      applicationField,
      selectedApplicationValue,
    );
  }, [
    findMatchedApplicationUsedHoursReasonEntry,
    formValues,
    sectionFields,
    sectionType,
  ]);

  const resolvedSectionFields = useMemo(() => {
    if (sectionType !== "savings") {
      return sectionFields;
    }

    const savingReasonOptions = buildSavingReasonOptionsFromLookup(
      matchedSavingLookupEntry,
    );

    return sectionFields.map((field) => {
      if (normalizeFieldIdentifier(field.fieldKey) !== "reasonid") {
        return field;
      }

      return {
        ...field,
        fieldValueList:
          savingReasonOptions.length > 0
            ? savingReasonOptions
            : field.fieldValueList,
      };
    });
  }, [matchedSavingLookupEntry, sectionFields, sectionType]);

  const shouldShowSavingOtherReasonField = useCallback(
    (candidateFormValues = formValues) => {
      if (sectionType !== "savings") {
        return true;
      }

      const otherReasonField = resolvedSectionFields.find(
        (field) => normalizeFieldIdentifier(field.fieldKey) === "otherreason",
      );
      const reasonField = resolvedSectionFields.find(
        (field) => normalizeFieldIdentifier(field.fieldKey) === "reasonid",
      );

      if (!otherReasonField || !reasonField) {
        return true;
      }

      const selectedReasonValue = Object.prototype.hasOwnProperty.call(
        candidateFormValues,
        reasonField.fieldKey,
      )
        ? (candidateFormValues[reasonField.fieldKey] ?? "")
        : reasonField.fieldValue || "";

      return isOtherLikeReasonSelection(reasonField, selectedReasonValue);
    },
    [formValues, resolvedSectionFields, sectionType],
  );

  const shouldShowSectionField = useCallback(
    (field, candidateFormValues = formValues) => {
      if (
        sectionType !== "savings" ||
        normalizeFieldIdentifier(field.fieldKey) !== "otherreason"
      ) {
        return true;
      }

      return shouldShowSavingOtherReasonField(candidateFormValues);
    },
    [formValues, sectionType, shouldShowSavingOtherReasonField],
  );

  const visibleSectionFields = useMemo(
    () =>
      resolvedSectionFields.filter((field) =>
        shouldShowSectionField(field, formValues),
      ),
    [formValues, resolvedSectionFields, shouldShowSectionField],
  );

  useEffect(() => {
    if (sectionFields.length === 0) {
      setFormValues({});
      setFieldValidationErrors({});
      return;
    }

    const initialFormValues = sectionFields.reduce((nextValues, field) => {
      nextValues[field.fieldKey] = field.fieldValue ?? "";
      return nextValues;
    }, {});

    setFormValues(initialFormValues);
    setFieldValidationErrors(buildValidationErrors(initialFormValues));
  }, [sectionFields]);

  function buildValidationErrors(candidateFormValues) {
    const candidateVisibleFields = resolvedSectionFields.filter((field) =>
      shouldShowSectionField(field, candidateFormValues),
    );
    const nextErrors = {};
    const allFieldValues = candidateVisibleFields.map((field) => ({
      fieldName: field.fieldKey,
      updatedValue: candidateFormValues[field.fieldKey] || "",
    }));

    candidateVisibleFields.forEach((field) => {
      const validationMessage = validateFieldValue(
        field.fieldKey,
        candidateFormValues[field.fieldKey] || "",
        allFieldValues,
      );

      if (validationMessage) {
        nextErrors[field.fieldKey] = validationMessage;
      }
    });

    return nextErrors;
  }

  const findSectionField = useCallback(
    (candidates = []) => {
      if (
        !Array.isArray(sectionConfig?.pagefields) ||
        candidates.length === 0
      ) {
        return null;
      }

      const normalizedCandidates = candidates
        .map(normalizeFieldIdentifier)
        .filter(Boolean);

      return (
        sectionConfig.pagefields.find((field) => {
          const normalizedFieldName = normalizeFieldIdentifier(field?.field);
          const normalizedLabel = normalizeFieldIdentifier(
            field?.labelToShowOnUI,
          );

          return normalizedCandidates.some(
            (candidate) =>
              candidate === normalizedFieldName ||
              candidate === normalizedLabel,
          );
        }) || null
      );
    },
    [sectionConfig],
  );

  const getSectionFieldValue = useCallback(
    (candidates = [], fallbackValue = "") => {
      const field = findSectionField(candidates);

      if (!field) {
        return fallbackValue;
      }

      if (Object.prototype.hasOwnProperty.call(formValues, field.field)) {
        return formValues[field.field] ?? "";
      }

      if (field.fieldValue !== undefined && field.fieldValue !== null) {
        return field.fieldValue;
      }

      return fallbackValue;
    },
    [findSectionField, formValues],
  );

  const buildEffortPayloadRecord = useCallback(
    () => ({
      EffortForId: getSectionFieldValue(["EffortForId"], ""),
      effortHours: getSectionFieldValue(["EffortHours", "Effort Hours"], ""),
      role: getSectionFieldValue(["Role"], ""),
      resourceName: getSectionFieldValue(["ResourceName", "Resource Name"], ""),
      reason: getSectionFieldValue(["Reason"], ""),
      effortForMasterId: getSectionFieldValue(
        ["EffortForMasterId", "EffortFor", "Effort For"],
        "",
      ),
    }),
    [getSectionFieldValue],
  );

  const buildSavingPayloadRecord = useCallback(() => {
    const resolvedUserName = getSectionFieldValue(
      ["UserName", "Username", "User Name"],
      "",
    );
    return {
      ID: getSectionFieldValue(["ID", "SavingId"], ""),
      hoursToSave: getSectionFieldValue(["HoursToSave", "Hours To Save"], ""),
      applicationUsedId: getSectionFieldValue(
        ["ApplicationUsedId", "Application Used"],
        "",
      ),
      otherReason: getSectionFieldValue(["OtherReason", "Other Reason"], ""),
      reasonId: getSectionFieldValue(["ReasonId", "Reason"], ""),
      userName: resolvedUserName || userEmail,
    };
  }, [getSectionFieldValue, userEmail]);

  // Helper to get all current field values for cross-field validation
  const getAllFieldValues = useCallback(() => {
    return resolvedSectionFields.map((field) => ({
      fieldName: field.fieldKey,
      updatedValue: formValues[field.fieldKey] || "",
    }));
  }, [formValues, resolvedSectionFields]);

  // Validate field value using fieldValidations.config rules
  const validateFieldValue = useCallback(
    (fieldKey, value, allFieldValues = []) => {
      const errors = [];
      const validation = getFieldValidation(fieldKey);
      const normalizedValue =
        typeof value === "string" ? value : String(value ?? "");

      // Check required validation
      if (validation?.required && (!value || String(value).trim() === "")) {
        errors.push(validation.requiredMessage || "This field is required");
        return errors.join(". ");
      }

      // If empty and not required, no errors
      if (!value || String(value).trim() === "") {
        return "";
      }

      // Regex validation from config
      if (validation?.regex && !validation.regex.test(normalizedValue)) {
        errors.push(validation.message || "Invalid format");
      }

      // Min/Max for numbers
      if (validation?.min !== undefined || validation?.max !== undefined) {
        const numValue = parseFloat(normalizedValue);
        if (!isNaN(numValue)) {
          if (validation.min !== undefined && numValue < validation.min) {
            errors.push(
              validation.message || `Value must be at least ${validation.min}`,
            );
          }
          if (validation.max !== undefined && numValue > validation.max) {
            errors.push(
              validation.message || `Value must be at most ${validation.max}`,
            );
          }
        }
      }

      // MinLength/MaxLength for text
      if (
        validation?.minLength !== undefined &&
        normalizedValue.length < validation.minLength
      ) {
        errors.push(
          validation.message ||
            `Minimum ${validation.minLength} characters required`,
        );
      }
      if (
        validation?.maxLength !== undefined &&
        normalizedValue.length > validation.maxLength
      ) {
        errors.push(
          validation.message ||
            `Maximum ${validation.maxLength} characters allowed`,
        );
      }

      // Contributor uniqueness validation
      if (isContributorField(fieldKey) && value) {
        const contributorValidation = validateContributorUniqueness(
          fieldKey,
          value,
          allFieldValues,
        );
        if (!contributorValidation.isValid) {
          errors.push(contributorValidation.message);
        }
      }

      return errors.join(". ");
    },
    [],
  );

  const handleFieldChange = (fieldKey, value) => {
    // Update form values first
    let updatedFormValues = {
      ...formValues,
      [fieldKey]: value,
    };

    if (
      sectionType === "savings" &&
      normalizeFieldIdentifier(fieldKey) === "applicationusedid"
    ) {
      const reasonField = resolvedSectionFields.find(
        (field) => normalizeFieldIdentifier(field.fieldKey) === "reasonid",
      );
      const otherReasonField = resolvedSectionFields.find(
        (field) => normalizeFieldIdentifier(field.fieldKey) === "otherreason",
      );
      const hoursField = resolvedSectionFields.find(
        (field) => normalizeFieldIdentifier(field.fieldKey) === "hourstosave",
      );
      const applicationField = resolvedSectionFields.find(
        (field) =>
          normalizeFieldIdentifier(field.fieldKey) === "applicationusedid",
      );
      const matchedApplicationLookup =
        findMatchedApplicationUsedHoursReasonEntry(applicationField, value);
      const defaultHoursValue = formatSavingDefaultHoursValue(
        matchedApplicationLookup?.defaultHours,
      );

      if (reasonField) {
        updatedFormValues[reasonField.fieldKey] = "";
      }

      if (otherReasonField) {
        updatedFormValues[otherReasonField.fieldKey] = "";
      }

      if (hoursField && defaultHoursValue) {
        updatedFormValues[hoursField.fieldKey] = defaultHoursValue;
      }
    }

    if (
      sectionType === "savings" &&
      normalizeFieldIdentifier(fieldKey) === "reasonid"
    ) {
      const reasonField = resolvedSectionFields.find(
        (field) => normalizeFieldIdentifier(field.fieldKey) === "reasonid",
      );
      const otherReasonField = resolvedSectionFields.find(
        (field) => normalizeFieldIdentifier(field.fieldKey) === "otherreason",
      );

      if (
        reasonField &&
        otherReasonField &&
        !isOtherLikeReasonSelection(reasonField, value)
      ) {
        updatedFormValues[otherReasonField.fieldKey] = "";
      }
    }

    setFormValues(updatedFormValues);
    setFieldValidationErrors(buildValidationErrors(updatedFormValues));
  };

  // Check if all fields have values
  const allFieldsFilled = useMemo(() => {
    if (visibleSectionFields.length === 0) return false;
    return visibleSectionFields.every((field) => {
      const value = formValues[field.fieldKey];
      return (
        value !== undefined && value !== null && String(value).trim() !== ""
      );
    });
    //  }, [formValues, resolvedSectionFields]);
  }, [formValues, visibleSectionFields]);

  const hasChangedSectionFields = useMemo(
    () =>
      visibleSectionFields.some((field) => {
        const updatedValue = formValues[field.fieldKey];

        if (updatedValue === undefined || updatedValue === null) {
          return false;
        }

        return (
          getComparableFieldValue(field, updatedValue) !==
          getComparableFieldValue(field, field.fieldValue || "")
        );
      }),
    [formValues, visibleSectionFields],
  );

  // Check if there are any validation errors
  const hasFieldValidationErrors =
    Object.keys(fieldValidationErrors).length > 0;

  const handleSubmit = async () => {
    if (!allFieldsFilled || !hasChangedSectionFields) return;

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      const updatedFields = visibleSectionFields.map((field) => ({
        fieldName: field.fieldKey,
        updatedValue: formValues[field.fieldKey] || "",
      }));

      const payload = {
        requestId:
          orderDetails?.[0]?.headerSection?.[0]?.requestId?.toString() || "0",
        lastModifiedEmployeeEmailID: userEmail,
        lastModifiedDate: new Date().toISOString(),
        currentStageId: 0,
        Role: currentUserRole,
        fields: [],
        efforts: sectionType === "efforts" ? [buildEffortPayloadRecord()] : [],
        savings: sectionType === "savings" ? [buildSavingPayloadRecord()] : [],
        agentType: "CHAT_AGENT",
      };

      const response = await OrdersService.updateOrder(payload);
      const validationWarnings = getUpdateOrderValidationWarnings(response);

      // AXON-CHAT-VALIDATION-WARNINGS: keep the form on the failed path when backend warnings come back in a resolved response.
      if (validationWarnings.length > 0) {
        setSubmitError("Validation warnings found.");

        if (onActionClick) {
          onActionClick({
            action: "updateFieldsFailed",
            error: {
              message: "Validation warnings found.",
              errors: validationWarnings,
            },
          });
        }
        return;
      }

      setSubmitSuccess(true);

      // Notify parent component of success with response message
      // The parent (Chatbot.jsx) handles refreshing order details and trail logs
      if (onActionClick) {
        onActionClick({
          action: "updateFieldsSuccess",
          fields: updatedFields,
          responseMessage:
            response?.message ||
            response?.Message ||
            "Order updated successfully",
        });
      }
    } catch (error) {
      console.error("Failed to update fields:", error);
      setSubmitError(
        error?.response?.data?.message ||
          "Failed to update fields. Please try again.",
      );

      if (onActionClick) {
        onActionClick({
          action: "updateFieldsFailed",
          error: error?.response?.data || error.message,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderFieldInput = (field) => {
    const value = Object.prototype.hasOwnProperty.call(
      formValues,
      field.fieldKey,
    )
      ? (formValues[field.fieldKey] ?? "")
      : field.fieldValue || "";
    const selectedFieldDisplayValue = getComparableFieldValue(field, value);
    const dataType = (field.dataType || "").toLowerCase();

    // Handle dropdown fields
    if (field.fieldValueList && field.fieldValueList.length > 0) {
      return (
        <select
          className="effort-field-select"
          value={value}
          onChange={(e) => handleFieldChange(field.fieldKey, e.target.value)}
          disabled={isSubmitting || submitSuccess}
          title={selectedFieldDisplayValue || undefined}
        >
          <option value="">Select {field.label}</option>
          {field.fieldValueList.map((option, idx) => {
            const optionValue =
              typeof option === "object"
                ? option.value || option.field || option.id
                : option;
            const optionLabel =
              typeof option === "object"
                ? option.label || option.field || option.value
                : option;
            return (
              <option key={optionValue || idx} value={optionValue}>
                {optionLabel}
              </option>
            );
          })}
        </select>
      );
    }

    // Handle active directory / email fields
    if (
      dataType === "activedirectory" ||
      dataType === "email" ||
      dataType === "active_directory"
    ) {
      return (
        <ActiveDirectoryUserSearch
          value={value}
          onChange={(email) => handleFieldChange(field.fieldKey, email)}
          disabled={isSubmitting || submitSuccess}
          placeholder={`Search for ${field.label}...`}
          fieldKey={field.fieldKey}
        />
      );
    }

    // Handle number fields
    if (dataType === "number" || dataType === "numerical") {
      return (
        <input
          type="number"
          className="effort-field-input"
          value={value}
          title={getInputTooltipTitle(value)}
          onChange={(e) => handleFieldChange(field.fieldKey, e.target.value)}
          placeholder={`Enter ${field.label}`}
          disabled={isSubmitting || submitSuccess}
        />
      );
    }

    // Default text input
    return (
      <input
        type="text"
        className="effort-field-input"
        value={value}
        title={getInputTooltipTitle(value)}
        onChange={(e) => handleFieldChange(field.fieldKey, e.target.value)}
        placeholder={`Enter ${field.label}`}
        disabled={isSubmitting || submitSuccess}
      />
    );
  };

  return (
    <div className={`order-chat-bubble ${message.role}`}>
      <div className="order-ai-card update-efforts-savings-card">
        <div className="order-ai-card-header">
          <div className="order-ai-card-title-logo">
            <img
              src={aiChatlogo}
              alt="LOGO"
              aria-hidden="true"
              className="order-ai-logo"
              style={{ width: 32, height: 32 }}
            />
          </div>
          <div className="order-ai-card-title-Avatar">
            <span>{message.header?.title || "Axon AI"}</span>
            <div className="order-ai-card-time">{`${day} ${time}`}</div>
          </div>
        </div>

        <div className="order-ai-card-body">
          <div className="update-efforts-savings-header">
            <p className="update-efforts-savings-text">
              Update {displayName} fields
            </p>
          </div>

          <div className="update-efforts-savings-instruction">
            <span className="emoji-icon">📝</span>
            {"  "}
            <p className="update-efforts-savings-instruction-text">
              Fill in all the fields below to update.
            </p>
          </div>
          <div className="update-efforts-savings-line" />

          <div className="effort-fields-form">
            {visibleSectionFields.map((field) => {
              const isReasonField = field.label
                .toLowerCase()
                .includes("reason");
              const fieldValidationError =
                fieldValidationErrors[field.fieldKey];
              return (
                <div
                  className={`effort-field-item ${isReasonField ? "effort-field-item--full" : ""}`}
                  key={field.fieldKey}
                >
                  <label className="effort-field-label">{field.label}</label>
                  {renderFieldInput(field)}
                  {fieldValidationError && (
                    <span className="effort-field-warning-text">
                      {fieldValidationError}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {submitError && (
            <div className="effort-fields-error">
              <span className="effort-fields-error-icon">⚠️</span>
              <span className="effort-fields-error-text">{submitError}</span>
            </div>
          )}

          {submitSuccess && (
            <div className="effort-fields-success">
              <span className="effort-fields-success-icon">✓</span>
              <span className="effort-fields-success-text">
                Fields updated successfully!
              </span>
            </div>
          )}

          <div className="effort-fields-actions">
            <button
              className="effort-submit-btn"
              onClick={handleSubmit}
              disabled={
                !allFieldsFilled ||
                isSubmitting ||
                submitSuccess ||
                hasFieldValidationErrors
              }
            >
              {isSubmitting
                ? "Updating..."
                : submitSuccess
                  ? "Updated"
                  : "Update Fields"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
